import { Box, Button, Divider, Typography } from '@mui/material';
import { CommonDialog, CommonDialogHeader } from '../../Dialog';
import { IOrderRerun } from '../../../types/api/response/orderrerunsresponse';

interface Props {
  rerun: IOrderRerun | null;
  isOpen: boolean;
  onClose: () => void;
}

const Field = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) => (
  <Box py={1.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? 12 : 'inherit',
        color: value == null ? 'text.disabled' : 'inherit',
      }}
    >
      {value ?? 'not assigned'}
    </Typography>
  </Box>
);

export const OrderRerunDetailDialog = ({ rerun, isOpen, onClose }: Props) => {
  if (!rerun) return null;

  const hasOverrides = !!(rerun.slot_start || rerun.packaging || rerun.comment_override);

  return (
    <CommonDialog
      isOpen={isOpen}
      header={<CommonDialogHeader title="Order Rerun Detail" onClose={onClose} />}
      body={
        <Box mt={1} sx={{ maxHeight: '65vh', overflowY: 'auto', pr: 0.5 }}>
          <Field
            label="Timestamp"
            value={rerun.created_at ? new Date(rerun.created_at).toLocaleString() : null}
          />
          <Divider />
          <Field label="Order Number (ORD-)" value={rerun.order_number} />
          <Divider />
          <Field label="DS Order Number (SO-)" value={rerun.ds_order_number} />
          <Divider />
          <Field label="Attempt #" value={rerun.sequence} />
          <Divider />
          <Field
            label="Source"
            value={rerun.source === 'ADMIN_PANEL' ? 'Admin Panel' : rerun.source}
          />
          <Divider />
          <Field label="Kind" value={rerun.kind} />
          {/* Only SuperPro reruns come from a tour — an admin-panel rerun has none. */}
          {rerun.source === 'SUPERPRO' && (
            <>
              <Divider />
              <Field label="SuperPro Tour Id" value={rerun.source_tour_id} mono />
              <Divider />
              <Field label="Locus Tour Id" value={rerun.source_locus_tour_id} mono />
            </>
          )}
          <Divider />
          <Field label="Requested By" value={rerun.requested_by} />
          <Divider />
          <Field label="Reason" value={rerun.reason} />
          <Divider />
          <Field label="Previous Locus Order Id" value={rerun.previous_locus_order_id} mono />
          <Divider />
          <Field label="New Locus Order Id" value={rerun.new_locus_order_id} mono />
          <Divider />
          {/* Set by the SuperPro ASSIGNED callback for this attempt. A first attempt has no rerun
              row, so its driver is only on the order detail. */}
          <Field label="Driver" value={rerun.driver_name} />
          <Divider />
          <Field label="Driver Phone" value={rerun.driver_phone} />
          <Divider />
          <Field label="Driver ID" value={rerun.driver_id} mono />
          {hasOverrides && (
            <>
              <Divider />
              <Field
                label="Slot Override"
                value={
                  rerun.slot_start && rerun.slot_end
                    ? `${new Date(rerun.slot_start).toLocaleString()} – ${new Date(rerun.slot_end).toLocaleString()}`
                    : null
                }
              />
              <Divider />
              <Field label="Packaging Override" value={rerun.packaging} />
              <Divider />
              <Field label="Comment Override" value={rerun.comment_override} />
            </>
          )}
        </Box>
      }
      footer={
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button variant="outlined" size="small" onClick={onClose}>
            Close
          </Button>
        </Box>
      }
    />
  );
};
